import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { Chip, Box, Button } from '@mui/material';
import { useState } from 'react';

/**
 * GapMatrixGrid Component
 * Display whole-person fact gaps
 */

interface PatientFactGap {
  key: string;
  domain: "CLINICAL" | "BEHAVIORAL" | "CULTURAL" | "FAMILY" | "SDOH";
  label: string;
  status: "MISSING" | "STALE" | "LOW_CONF" | "CONFLICT" | "OK";
  gapScore: number; // 0-1
  confidence?: number;
  lastUpdated?: string;
  reason?: string;
}

interface GapMatrixGridProps {
  gaps: PatientFactGap[];
  onRecommendQuestions?: (gaps: PatientFactGap[]) => void;
}

export const GapMatrixGrid: React.FC<GapMatrixGridProps> = ({
  gaps,
  onRecommendQuestions
}) => {
  const [selectedGaps, setSelectedGaps] = useState<GridRowSelectionModel>([]);

  const getStatusColor = (status: string): "error" | "warning" | "info" | "secondary" | "success" | "default" => {
    const colors: Record<string, "error" | "warning" | "info" | "secondary" | "success"> = {
      MISSING: 'error',
      STALE: 'warning',
      LOW_CONF: 'info',
      CONFLICT: 'secondary',
      OK: 'success'
    };
    return colors[status] || 'default';
  };

  const getDomainColor = (domain: string): "primary" | "secondary" | "success" | "warning" | "error" | "default" => {
    const colors: Record<string, "primary" | "secondary" | "success" | "warning" | "error"> = {
      CLINICAL: 'primary',
      BEHAVIORAL: 'secondary',
      CULTURAL: 'success',
      FAMILY: 'warning',
      SDOH: 'error'
    };
    return colors[domain] || 'default';
  };

  const columns: GridColDef[] = [
    {
      field: 'domain',
      headerName: 'Domain',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value as string}
          size="small"
          color={getDomainColor(params.value as string)}
        />
      )
    },
    {
      field: 'label',
      headerName: 'Fact',
      width: 300
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value as string}
          size="small"
          color={getStatusColor(params.value as string)}
        />
      )
    },
    {
      field: 'gapScore',
      headerName: 'Gap Score',
      width: 130,
      valueFormatter: (params) => `${((params.value as number) * 100).toFixed(0)}%`
    },
    {
      field: 'confidence',
      headerName: 'Confidence',
      width: 130,
      valueFormatter: (params) =>
        params.value ? `${((params.value as number) * 100).toFixed(0)}%` : "—"
    },
    {
      field: 'lastUpdated',
      headerName: 'Last Updated',
      width: 150,
      valueFormatter: (params) =>
        params.value ? new Date(params.value as string).toLocaleDateString() : "Never"
    },
    {
      field: 'reason',
      headerName: 'Reason',
      width: 250,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: 'normal', py: 1 }}>
          {params.value || "—"}
        </Box>
      )
    }
  ];

  const handleRecommend = () => {
    const selected = gaps.filter((gap) => selectedGaps.includes(gap.key));
    onRecommendQuestions?.(selected);
  };

  return (
    <Box>
      {onRecommendQuestions && selectedGaps.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            onClick={handleRecommend}
          >
            Generate Questions ({selectedGaps.length} selected)
          </Button>
        </Box>
      )}

      <DataGrid
        rows={gaps}
        columns={columns}
        getRowId={(row) => row.key}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 }
          }
        }}
        pageSizeOptions={[25, 50, 100]}
        checkboxSelection={!!onRecommendQuestions}
        onRowSelectionModelChange={setSelectedGaps}
        rowSelectionModel={selectedGaps}
        autoHeight
        rowHeight={60}
      />
    </Box>
  );
};
